import { Link } from "@inertiajs/react"
import { Lock } from "lucide-react"
import { useTranslation } from "react-i18next"

import { BadgeGrid } from "@/components/badges/badge-grid"
import { BadgeTile } from "@/components/badges/badge-tile"
import { usePreviewMode } from "@/components/profile-preview/preview-mode"
import { Card, CardContent } from "@/components/ui/card"
import { isNewlyEarned, tierFor } from "@/lib/badge-tiers"
import { creatorBadgesPath } from "@/routes"
import type { RecentBadgeTile } from "@/types/badges"

/**
 * OKS-50 — `/profile` "My Badges" preview block (UX Spec §2.1).
 *
 * Renders top-N earned badges as 4-col fixed grid. When `recent` is
 * empty, renders a single locked-tile placeholder + "Earn your first
 * badge…" caption (UX Spec §3, empty state).
 *
 * "View all" link is always present (deep-links to `/profile/badges`)
 * — even when empty, the link takes the creator to the locked-grid
 * page which surfaces the milestone catalogue. Total count drives
 * the i18n interpolation when > 4 badges have been earned.
 */
interface BadgePreviewBlockProps {
  recent: RecentBadgeTile[]
}

export function BadgePreviewBlock({ recent }: BadgePreviewBlockProps) {
  const { t } = useTranslation()
  const preview = usePreviewMode()

  return (
    <Card data-testid="badge-preview-block" className="border-0 shadow-none">
      <CardContent className="flex flex-col gap-3 px-0 py-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">
            {t("badges.preview.heading")}
          </h2>
          {/* OKS-400 — inert in read-only preview (no navigation). */}
          {preview ? (
            <span
              aria-disabled="true"
              className="text-muted-foreground/60 cursor-not-allowed text-sm"
              data-testid="badge-preview-view-all"
            >
              {t("badges.preview.view_all")}
            </span>
          ) : (
            <Link
              href={creatorBadgesPath()}
              className="text-muted-foreground hover:text-foreground text-sm"
              data-testid="badge-preview-view-all"
            >
              {t("badges.preview.view_all")}
            </Link>
          )}
        </div>

        {recent.length === 0 ? (
          <EmptyState />
        ) : (
          <BadgeGrid preview>
            {recent.map((tile) => {
              const tier = tierFor({
                kind: tile.kind,
                identifier: tile.identifier,
                accentColor: tile.accent_color,
              })
              const isCustom = tile.kind === "custom"
              const name =
                tile.kind === "milestone"
                  ? t(`badges.milestones.${tile.i18n_key}.name`)
                  : (tile.name ?? "")
              return (
                <BadgeTile
                  key={tile.identifier}
                  // OKS-573 — thread the resolved image for BOTH kinds:
                  // customs force `variant="image"`; milestones render the
                  // brand override when present (else the medallion).
                  variant={isCustom ? "image" : "medallion"}
                  imageUrl={tile.image_url}
                  icon={tile.icon}
                  accentColor={tile.accent_color}
                  name={name}
                  tier={tier}
                  // OKS-937 — `RecentBadgeTile` has no description field
                  // at all, so this surface cannot use `tile_earned` (it
                  // interpolates %{description}). Structural absence, not
                  // a blank value: a blank description is unconstructable
                  // (Badge validates presence + length >= 1, and the DB
                  // carries CHECK char_length >= 1).
                  // Fixes the live gap where this preview announced no
                  // earned state, while the same badge on /profile/badges
                  // announced "— obtenu".
                  ariaLabel={t("badges.aria.tile_earned_brief", {
                    name,
                    tier: t(`badges.tier.${tier}`),
                  })}
                  size="preview"
                  isNew={isNewlyEarned(tile.earned_at)}
                  newLabel={t("badges.new")}
                  earnedAt={tile.earned_at}
                />
              )
            })}
          </BadgeGrid>
        )}
      </CardContent>
    </Card>
  )
}

function EmptyState() {
  const { t } = useTranslation()
  return (
    <div
      data-testid="badge-preview-empty"
      className="bg-muted/40 text-muted-foreground flex items-center gap-3 rounded-lg border p-4 text-sm"
    >
      <div className="bg-muted flex h-10 w-10 items-center justify-center rounded-full">
        <Lock className="h-5 w-5" aria-hidden="true" />
      </div>
      <p>{t("badges.preview.empty")}</p>
    </div>
  )
}
